import axios from 'axios';
import Button from 'react-bootstrap/Button';
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import Container from 'react-bootstrap/Container';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Card from 'react-bootstrap/Card';
import ListGroup from 'react-bootstrap/ListGroup';
import Table from 'react-bootstrap/Table';
import { Helmet } from 'react-helmet-async';
import LoadingBox from '../../component/LoadingBox';
import MessageBox from '../../component/MessageBox';
import { Store } from '../../Store';
import { toast } from 'react-toastify';
import Form from 'react-bootstrap/Form';
import Badge from 'react-bootstrap/Badge';
import { getError } from '../../utils';
import Alert from 'react-bootstrap/Alert';
import { api } from '../../Api';

/**
 * WITHDRAW HISTORY — day by day.
 *
 * Opens on TODAY's requests (Dhaka business day), with every earlier date that
 * has requests listed on the left, newest first, so the office works through
 * the queue one day at a time. Totals are shown in points and in BDT (the
 * server sends the rate — 1 point = Tk 12). Paying/approving works exactly as
 * before: the same /withdraw/adminpay call.
 *
 * Shared by /admin/withdraw and /agent/withdraw.
 */

const STATUS_TABS = [
  { value: 'pending', label: 'Pending (not paid yet)', variant: 'danger' },
  { value: 'paid', label: 'Paid', variant: 'success' },
  { value: 'all', label: 'All', variant: 'secondary' },
];
const KIND_LABEL = { agent: 'Agent withdraw', self_topup: 'Self Topup', merchant: 'Merchant withdraw' };
const METHOD_LABEL = { self_topup: 'Self Topup', merchant: 'Merchant', none: 'Not recorded' };

const fmt = (v, digits = 2) =>
  Number(v || 0).toLocaleString('en-US', { maximumFractionDigits: digits });
const pt = (v) => `${fmt(v)} pt`;
const tk = (v) => `৳ ${fmt(v)}`;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
function prettyDate(iso) {
  if (!iso) return '';
  const d = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return `${DAYS[d.getDay()]}, ${String(d.getDate()).padStart(2, '0')} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}
function shiftDay(iso, n) {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function dayName(iso, today) {
  if (!iso) return '';
  if (iso === today) return 'Today';
  if (today && iso === shiftDay(today, -1)) return 'Yesterday';
  return prettyDate(iso);
}
function timeOf(v) {
  if (!v) return '—';
  return new Date(v).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Dhaka',
  });
}

/** A small headline number. */
function StatCard({ label, value, sub, bg = 'light', text }) {
  return (
    <Card bg={bg} text={text} className="h-100 shadow-sm">
      <Card.Body className="py-2 px-3">
        <div className="small" style={{ opacity: 0.8 }}>{label}</div>
        <div className="fs-5 fw-bold">{value}</div>
        {sub ? <div className="small" style={{ opacity: 0.8 }}>{sub}</div> : null}
      </Card.Body>
    </Card>
  );
}

/** One "split by …" table (method / type / source). */
function BreakdownTable({ title, rows, labelOf }) {
  if (!rows || !rows.length) return null;
  return (
    <Card className="h-100 shadow-sm">
      <Card.Header className="py-1 small fw-bold">{title}</Card.Header>
      <Table size="sm" className="mb-0 small" responsive>
        <thead>
          <tr>
            <th></th>
            <th className="text-end">Requests</th>
            <th className="text-end">Net point</th>
            <th className="text-end">Net BDT</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key}>
              <td>{labelOf ? labelOf(r.key) : r.key}</td>
              <td className="text-end">{r.count}</td>
              <td className="text-end">{fmt(r.net)}</td>
              <td className="text-end">{fmt(r.netBdt)}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Card>
  );
}

export default function AdminWithdrawScreen() {
  const { state } = useContext(Store);
  const { userInfo } = state;
  const auth = { headers: { Authorization: `Bearer ${userInfo.token}` } };

  // ---- filters ---------------------------------------------------------
  const [status, setStatus] = useState('pending');
  const [date, setDate] = useState(''); // '' = server default (today)
  const [kind, setKind] = useState('');
  const [method, setMethod] = useState('');
  const [source, setSource] = useState('');
  const [q, setQ] = useState('');
  const [qInput, setQInput] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [fromInput, setFromInput] = useState('');
  const [toInput, setToInput] = useState('');

  // ---- data ------------------------------------------------------------
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [hiddenRows, setHiddenRows] = useState([]);

  const params = useMemo(() => {
    const p = new URLSearchParams({ status });
    if (date) p.set('date', date);
    if (kind) p.set('kind', kind);
    if (method) p.set('method', method);
    if (source) p.set('source', source);
    if (q) p.set('q', q);
    if (from) p.set('from', from);
    if (to) p.set('to', to);
    return p.toString();
  }, [status, date, kind, method, source, q, from, to]);

  async function loadReport() {
    try {
      setLoading(true);
      const { data } = await axios.get(api + `/api/availablebalance/withdraw/report?${params}`, auth);
      setReport(data);
      setError('');
      setSelectedIds([]);
      setHiddenRows([]);
    } catch (err) {
      setError(getError(err));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, userInfo]);

  // ---- existing actions: view payout details / pay ----------------------
  const [table, setTable] = useState(1);
  const [receiverName, setReceiverName] = useState();
  const [paymentMethod, setPaymentMethod] = useState();
  const [netAmount, setNetAmount] = useState();
  const [bankInfo, setBankInfo] = useState();
  async function view(userId, wId) {
    setTable(0);
    try {
      const { data } = await axios.post(api + '/api/availablebalance/withdrawinfo', { id: userId }, auth);
      setReceiverName(data.username);
      setPaymentMethod(data.paymentmethod);
      setNetAmount(data.amount);

      if (data.paymentmethod === 'self_topup') {
        setBankInfo('No external payment — released straight to the member\'s Shopping Balance.');
      } else if (data.paymentmethod === 'merchant') {
        setBankInfo('No bank/mobile-banking details needed — paid directly by the merchant.');
      } else {
        receiverInfo(wId, data.paymentmethod);
      }
    } catch (error) {
      toast.error(getError(error));
    }
  }

  async function receiverInfo(wId, method) {
    setBankInfo('Loading...');
    const { data } = await axios.post(api + '/api/availablebalance/user', { id: wId }, auth);
    if (method !== 'bank') {
      setBankInfo(data.mobilebankname + ', Number: ' + data.mobilebankaccount);
    } else {
      setBankInfo(data.bankname + ', ' + data.bankbranch + 'Branch, Account Number: ' + data.bankaccount);
    }
  }

  async function paid(id, username, paymentmethod, paidby, userId) {
    try {
      await axios.put(
        api + `/api/availablebalance/withdraw/adminpay`,
        { id, username, paymentmethod, paidby, userId },
        auth
      );
      toast.success(`Paid @${username}`);
      // Hide the row at once, then refresh the totals.
      setHiddenRows((prev) => [...prev, id]);
      setTimeout(loadReport, 600);
    } catch (error) {
      toast.error(getError(error));
    }
  }

  // ---- derived ---------------------------------------------------------
  const today = report?.today;
  const rate = report?.rate?.tkPerPoint || 12;
  const rateLabel = report?.rate?.label || '1 point = Tk 12';
  const sourceLabels = report?.sourceLabels || {};
  const day = report?.day;
  const records = (day?.records || []).filter((r) => !hiddenRows.includes(r._id));
  const dateBy = report?.filters?.dateBy || 'request';

  // Today always sits on top of the list, even with nothing in it.
  const dayList = useMemo(() => {
    const list = report?.days || [];
    if (!today || report?.filters?.from) return list;
    if (list.some((d) => d.date === today)) return list;
    return [{ date: today, count: 0, net: 0, netBdt: 0, empty: true }, ...list];
  }, [report, today]);

  // Records grouped by date with a subtotal per date.
  const groups = useMemo(() => {
    const m = new Map();
    for (const r of records) {
      const k = r.day || day?.date;
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(r);
    }
    return [...m.entries()].map(([k, rows]) => ({
      date: k,
      rows,
      gross: rows.reduce((s, r) => s + Number(r.gross || 0), 0),
      charge: rows.reduce((s, r) => s + Number(r.charge || 0), 0),
      net: rows.reduce((s, r) => s + Number(r.amount || 0), 0),
    }));
  }, [records, day]);

  const methodOptions = useMemo(() => {
    const keys = new Set((report?.overall?.byMethod || []).map((m) => m.key));
    if (method) keys.add(method);
    return [...keys].filter((k) => k !== 'none').sort();
  }, [report, method]);
  const sourceOptions = useMemo(() => {
    const keys = new Set((report?.overall?.bySource || []).map((m) => m.key));
    if (source) keys.add(source);
    return [...keys].sort();
  }, [report, source]);

  const selectableIds = records.filter((r) => !r.status && r.withdrawKind !== 'merchant').map((r) => r._id);
  const allIds = records.map((r) => r._id);
  const allSelected = allIds.length > 0 && allIds.every((id) => selectedIds.includes(id));

  const dayPdfUrl = `${api}/api/availablebalance/withdraw/report.pdf?${params}${
    day && !day.isRange && !date ? `&date=${day.date}` : ''
  }&token=${userInfo.token}&download=1`;

  function applySearch(e) {
    e && e.preventDefault();
    setQ(qInput.trim());
    const f = fromInput;
    const t = toInput || fromInput;
    if (f) {
      setFrom(f <= t ? f : t);
      setTo(f <= t ? t : f);
      setDate(f === t ? f : 'range');
    } else {
      setFrom('');
      setTo('');
    }
  }
  function clearFilters() {
    setQ(''); setQInput(''); setKind(''); setMethod(''); setSource('');
    setFrom(''); setTo(''); setFromInput(''); setToInput(''); setDate('');
  }
  function changeStatus(v) {
    setStatus(v);
    setDate(from ? date : '');
  }

  const filtersOn = Boolean(q || kind || method || source || from);
  const t = day?.totals;
  const pending = report?.pending;
  const overall = report?.overall;
  const viewTitle = day
    ? day.isRange
      ? `${prettyDate(report.filters.from)} – ${prettyDate(report.filters.to)}`
      : `${dayName(day.date, today)}${day.date === today || day.date === shiftDay(today || '', -1) ? ` · ${prettyDate(day.date)}` : ''}`
    : '';

  return (
    <div>
      <Helmet>
        <title>Withdraw History</title>
      </Helmet>
      <Container fluid="xl">
        {/* ---------------- heading + status tabs ---------------- */}
        <Row className="my-3 align-items-center g-2">
          <Col md={5}>
            <h2 className="mb-0">Withdraw History</h2>
            <div className="text-muted small">
              Day by day (Bangladesh time) · {rateLabel}
            </div>
          </Col>
          <Col md={7} className="text-md-end">
            <ButtonGroup>
              {STATUS_TABS.map((s) => (
                <Button
                  key={s.value}
                  variant={status === s.value ? s.variant : `outline-${s.variant}`}
                  onClick={() => changeStatus(s.value)}
                >
                  {s.label}
                </Button>
              ))}
            </ButtonGroup>
          </Col>
        </Row>

        {/* ---------------- filters ---------------- */}
        <Card className="mb-3 shadow-sm">
          <Card.Body className="py-2">
            <Form onSubmit={applySearch}>
              <Row className="g-2 align-items-end">
                <Col xs={12} md={2}>
                  <Form.Label className="small mb-0">Username</Form.Label>
                  <Form.Control
                    size="sm"
                    placeholder="Search username"
                    value={qInput}
                    onChange={(e) => setQInput(e.target.value)}
                  />
                </Col>
                <Col xs={6} md={2}>
                  <Form.Label className="small mb-0">From date</Form.Label>
                  <Form.Control size="sm" type="date" value={fromInput} onChange={(e) => setFromInput(e.target.value)} />
                </Col>
                <Col xs={6} md={2}>
                  <Form.Label className="small mb-0">To date</Form.Label>
                  <Form.Control size="sm" type="date" value={toInput} onChange={(e) => setToInput(e.target.value)} />
                </Col>
                <Col xs={6} md={2}>
                  <Form.Label className="small mb-0">Withdraw to</Form.Label>
                  <Form.Select size="sm" value={kind} onChange={(e) => setKind(e.target.value)}>
                    <option value="">All destinations</option>
                    <option value="agent">Agent withdraw</option>
                    <option value="self_topup">Self Topup</option>
                    <option value="merchant">Merchant withdraw</option>
                  </Form.Select>
                </Col>
                <Col xs={6} md={1}>
                  <Form.Label className="small mb-0">Method</Form.Label>
                  <Form.Select size="sm" value={method} onChange={(e) => setMethod(e.target.value)}>
                    <option value="">All</option>
                    {methodOptions.map((m) => (
                      <option key={m} value={m}>{METHOD_LABEL[m] || m}</option>
                    ))}
                  </Form.Select>
                </Col>
                <Col xs={6} md={1}>
                  <Form.Label className="small mb-0">From balance</Form.Label>
                  <Form.Select size="sm" value={source} onChange={(e) => setSource(e.target.value)}>
                    <option value="">All</option>
                    {sourceOptions.map((s) => (
                      <option key={s} value={s}>{sourceLabels[s] || s}</option>
                    ))}
                  </Form.Select>
                </Col>
                <Col xs={6} md={1}>
                  <Button type="submit" size="sm" variant="primary" className="w-100">Search</Button>
                </Col>
                <Col xs={6} md={1}>
                  <Button size="sm" variant="outline-secondary" className="w-100" onClick={clearFilters} disabled={!filtersOn && !date}>
                    Clear
                  </Button>
                </Col>
              </Row>
            </Form>
          </Card.Body>
        </Card>

        {/* ---------------- overall pending ---------------- */}
        {pending ? (
          <Row className="g-2 mb-3">
            <Col xs={6} md={3}>
              <StatCard
                bg="danger"
                text="white"
                label="All pending (any date)"
                value={`${pending.count} requests`}
                sub={pending.oldestDate ? `Oldest: ${prettyDate(pending.oldestDate)}` : 'Nothing waiting'}
              />
            </Col>
            <Col xs={6} md={3}>
              <StatCard bg="danger" text="white" label="Pending to pay (point)" value={pt(pending.net)} sub={`Gross ${pt(pending.gross)}`} />
            </Col>
            <Col xs={6} md={3}>
              <StatCard bg="danger" text="white" label="Pending to pay (BDT)" value={tk(pending.netBdt)} sub={rateLabel} />
            </Col>
            <Col xs={6} md={3}>
              {status !== 'pending' && overall ? (
                <StatCard
                  label={`All ${status === 'paid' ? 'paid' : 'requests'} ${filtersOn ? '(this filter)' : '(any date)'}`}
                  value={`${overall.count} · ${pt(overall.net)}`}
                  sub={tk(overall.netBdt)}
                />
              ) : (
                <StatCard label="Charge kept on pending" value={pt(pending.charge)} sub={tk(pending.chargeBdt)} />
              )}
            </Col>
          </Row>
        ) : null}

        {error ? <MessageBox variant="danger">{error}</MessageBox> : null}

        <Row className="g-3">
          {/* ---------------- date list ---------------- */}
          <Col md={3}>
            <Card className="shadow-sm">
              <Card.Header className="py-2">
                <div className="fw-bold">Dates</div>
                <div className="small text-muted">
                  {dateBy === 'paid' ? 'By the day it was paid' : 'By the day it was requested'}
                </div>
                <Form.Control
                  size="sm"
                  type="date"
                  className="mt-1"
                  value={day && !day.isRange ? day.date : ''}
                  onChange={(e) => e.target.value && setDate(e.target.value)}
                  title="Jump to a date"
                />
              </Card.Header>
              <ListGroup variant="flush" style={{ maxHeight: 620, overflowY: 'auto' }}>
                {from ? (
                  <ListGroup.Item action active={date === 'range'} onClick={() => setDate('range')}>
                    <div className="fw-bold">Whole range</div>
                    <div className="small">
                      {report?.range?.totals?.count || 0} requests · {pt(report?.range?.totals?.net)} · {tk(report?.range?.totals?.netBdt)}
                    </div>
                  </ListGroup.Item>
                ) : null}
                {dayList.map((d) => {
                  const active = day && !day.isRange && day.date === d.date;
                  return (
                    <ListGroup.Item key={d.date} action active={active} onClick={() => setDate(d.date)}>
                      <div className="d-flex justify-content-between">
                        <span className="fw-bold">{dayName(d.date, today)}</span>
                        <Badge bg={active ? 'light' : d.count ? 'danger' : 'secondary'} text={active ? 'dark' : undefined}>
                          {d.count}
                        </Badge>
                      </div>
                      {d.date === today || d.date === shiftDay(today || '', -1) ? (
                        <div className="small" style={{ opacity: 0.75 }}>{prettyDate(d.date)}</div>
                      ) : null}
                      <div className="small">
                        {d.empty ? 'No requests yet' : `${pt(d.net)} · ${tk(d.netBdt)}`}
                      </div>
                      {status === 'all' && !d.empty ? (
                        <div className="small" style={{ opacity: 0.75 }}>
                          {d.pendingCount} pending · {d.paidCount} paid
                        </div>
                      ) : null}
                    </ListGroup.Item>
                  );
                })}
                {!dayList.length ? <ListGroup.Item className="text-muted small">No dates match.</ListGroup.Item> : null}
                {report?.daysTruncated ? (
                  <ListGroup.Item className="text-muted small">
                    Only the latest {dayList.length} dates are listed — use From/To to see older ones.
                  </ListGroup.Item>
                ) : null}
              </ListGroup>
            </Card>
          </Col>

          {/* ---------------- selected day ---------------- */}
          <Col md={9}>
            {loading && !report ? (
              <LoadingBox />
            ) : day ? (
              <>
                <div className="d-flex flex-wrap justify-content-between align-items-center mb-2 gap-2">
                  <div>
                    <h4 className="mb-0">{viewTitle}</h4>
                    <div className="small text-muted">
                      {STATUS_TABS.find((s) => s.value === status)?.label} withdrawals
                      {filtersOn ? ' · filtered' : ''}
                      {loading ? ' · refreshing…' : ''}
                    </div>
                  </div>
                  <div className="d-flex gap-2 flex-wrap">
                    {!day.isRange ? (
                      <>
                        <Button size="sm" variant="outline-secondary" onClick={() => setDate(shiftDay(day.date, -1))}>
                          ‹ Previous day
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          disabled={day.date >= today}
                          onClick={() => setDate(shiftDay(day.date, 1))}
                        >
                          Next day ›
                        </Button>
                      </>
                    ) : null}
                    {day.date !== today || day.isRange ? (
                      <Button size="sm" variant="outline-primary" onClick={() => { setDate(''); setFrom(''); setTo(''); setFromInput(''); setToInput(''); }}>
                        Today
                      </Button>
                    ) : null}
                    <a className="btn btn-sm btn-success" target="_blank" rel="noreferrer" href={dayPdfUrl}>
                      Download this {day.isRange ? 'range' : 'day'} (PDF)
                    </a>
                  </div>
                </div>

                <Row className="g-2 mb-3">
                  <Col xs={6} md={2}><StatCard label="Requests" value={t.count} sub={status === 'all' ? `${t.pendingCount} pending · ${t.paidCount} paid` : null} /></Col>
                  <Col xs={6} md={2}><StatCard label="Gross point" value={fmt(t.gross)} sub="before charge" /></Col>
                  <Col xs={6} md={2}><StatCard label="Charge" value={fmt(t.charge)} sub={tk(t.chargeBdt)} /></Col>
                  <Col xs={6} md={3}><StatCard bg="primary" text="white" label="Net point (to pay)" value={pt(t.net)} /></Col>
                  <Col xs={12} md={3}><StatCard bg="success" text="white" label="Net BDT (to pay)" value={tk(t.netBdt)} sub={rateLabel} /></Col>
                </Row>

                {t.count ? (
                  <Row className="g-2 mb-3">
                    <Col md={4}>
                      <BreakdownTable title="By payment method" rows={day.byMethod} labelOf={(k) => METHOD_LABEL[k] || k} />
                    </Col>
                    <Col md={4}>
                      <BreakdownTable title="By withdraw type" rows={day.byKind} labelOf={(k) => KIND_LABEL[k] || k} />
                    </Col>
                    <Col md={4}>
                      <BreakdownTable title="By balance it came from" rows={day.bySource} labelOf={(k) => sourceLabels[k] || k} />
                    </Col>
                  </Row>
                ) : null}

                {/* view/confirm panel (unchanged behaviour) */}
                <Alert variant={table ? 'success d-none' : 'success'}>
                  <Alert.Heading>Confirming Withdraw</Alert.Heading>
                  <p>Username : {receiverName}</p>
                  <p>Payment Method : {paymentMethod}</p>
                  <p>Net Amount : {netAmount}</p>
                  <p>Bank Info : {bankInfo}</p>
                  <hr />
                  <p className="mb-0">
                    <Button variant="outline-secondary" onClick={() => setTable(1)}>Close</Button>
                  </p>
                </Alert>

                {selectedIds.length > 0 && (
                  <div className="d-flex justify-content-end mb-2">
                    <a
                      className="btn btn-outline-success btn-sm"
                      target="_blank"
                      rel="noreferrer"
                      href={`${api}/api/invoices/withdraw/bulk.pdf?ids=${selectedIds.join(',')}&token=${userInfo.token}&download=1`}
                    >
                      Download {selectedIds.length} selected as PDF
                    </a>
                  </div>
                )}

                {day.recordsTruncated ? (
                  <MessageBox variant="warning">
                    Showing the first {day.records.length} requests only. Narrow the dates or filters to see the rest.
                  </MessageBox>
                ) : null}

                {!records.length ? (
                  <MessageBox variant="info">
                    {status === 'pending'
                      ? `No pending withdraw requests ${day.isRange ? 'in this range' : `on ${day.date === today ? 'today' : prettyDate(day.date)}`}.`
                      : 'No withdraw requests for this selection.'}
                    {status === 'pending' && pending?.count ? ' Pick an earlier date on the left to see older pending requests.' : ''}
                  </MessageBox>
                ) : (
                  <div className={table ? 'table-responsive' : 'table-responsive d-none'}>
                    <table className="table table-striped table-sm align-middle">
                      <thead className="table-light">
                        <tr>
                          <th>
                            <Form.Check
                              title="Select all"
                              checked={allSelected}
                              onChange={(e) => setSelectedIds(e.target.checked ? allIds : [])}
                            />
                          </th>
                          <th>Member</th>
                          <th>Destination</th>
                          <th>Method</th>
                          <th className="text-end">Gross pt</th>
                          <th className="text-end">Charge</th>
                          <th className="text-end">Net pt</th>
                          <th className="text-end">Net BDT</th>
                          <th>Requested</th>
                          <th>Status</th>
                          <th>View</th>
                          <th>Action</th>
                          <th>Report</th>
                        </tr>
                      </thead>
                      <tbody>
                        {groups.map((g) => (
                          <React.Fragment key={g.date}>
                            <tr className="table-secondary">
                              <td colSpan={4} className="fw-bold">
                                {dayName(g.date, today)}
                                {g.date === today ? ` · ${prettyDate(g.date)}` : ''} — {g.rows.length} request{g.rows.length === 1 ? '' : 's'}
                              </td>
                              <td className="text-end fw-bold">{fmt(g.gross)}</td>
                              <td className="text-end fw-bold">{fmt(g.charge)}</td>
                              <td className="text-end fw-bold">{fmt(g.net)}</td>
                              <td className="text-end fw-bold">{fmt(g.net * rate)}</td>
                              <td colSpan={5}></td>
                            </tr>
                            {g.rows.map((product) => (
                              <tr key={product._id}>
                                <td>
                                  <Form.Check
                                    checked={selectedIds.includes(product._id)}
                                    onChange={(e) =>
                                      setSelectedIds((prev) =>
                                        e.target.checked ? [...prev, product._id] : prev.filter((id) => id !== product._id)
                                      )
                                    }
                                  />
                                </td>
                                <td>
                                  <div className="fw-bold">{product.username}</div>
                                  <div className="small text-muted">
                                    {[product.memberName, product.position, product.memberPhone].filter(Boolean).join(' · ')}
                                  </div>
                                </td>
                                <td>
                                  {product.withdrawKind === 'merchant' ? (
                                    <div className="d-flex align-items-center gap-1">
                                      {product.merchantLogo && (
                                        <img
                                          src={product.merchantLogo}
                                          alt=""
                                          style={{ width: 20, height: 20, borderRadius: '50%', objectFit: 'cover' }}
                                        />
                                      )}
                                      <Badge bg="primary">{product.merchantName || product.to} · Merchant</Badge>
                                    </div>
                                  ) : product.to === 'Self Topup' ? (
                                    <Badge bg="info">Self Topup</Badge>
                                  ) : (
                                    product.to
                                  )}
                                  {product.withdrawsource ? (
                                    <div className="small text-muted">
                                      from {sourceLabels[product.withdrawsource] || product.withdrawsource}
                                    </div>
                                  ) : null}
                                </td>
                                <td>
                                  {product.paymentmethod === 'self_topup' || product.paymentmethod === 'merchant'
                                    ? '—'
                                    : product.paymentmethod}
                                </td>
                                <td className="text-end">{fmt(product.gross)}</td>
                                <td className="text-end">{fmt(product.charge)}</td>
                                <td className="text-end fw-bold">{fmt(product.amount)}</td>
                                <td className="text-end">{fmt(product.netBdt)}</td>
                                <td className="small">{timeOf(product.createdAt)}</td>
                                <td>
                                  {product.status ? (
                                    <Badge bg="success" title={product.paidBy ? `by ${product.paidBy}` : ''}>
                                      Paid {timeOf(product.paidAt)}
                                    </Badge>
                                  ) : (
                                    <Badge bg="danger">Pending</Badge>
                                  )}
                                </td>
                                <td>
                                  <Button size="sm" variant="info" onClick={() => view(product._id, product.userId)}>
                                    View
                                  </Button>
                                </td>
                                <td>
                                  {product.withdrawKind === 'merchant' ? (
                                    <Badge bg={product.status ? 'success' : 'secondary'}>
                                      {product.status ? 'Paid by merchant' : 'Awaiting merchant'}
                                    </Badge>
                                  ) : (
                                    <Button
                                      size="sm"
                                      variant={product.status ? 'success' : 'danger'}
                                      className={product.status ? 'disabled' : ''}
                                      onClick={() =>
                                        paid(product._id, product.username, product.paymentmethod, userInfo.username, product.userId)
                                      }
                                    >
                                      {!product.status && product.to === 'Self Topup' ? 'Credit Self Topup' : product.status ? 'Paid' : 'Pay Now'}
                                    </Button>
                                  )}
                                </td>
                                <td>
                                  <a
                                    className="btn btn-info btn-sm"
                                    target="_blank"
                                    rel="noreferrer"
                                    href={`${api}/api/invoices/withdraw/${product._id}.pdf?token=${userInfo.token}&download=1`}
                                  >
                                    PDF
                                  </a>
                                </td>
                              </tr>
                            ))}
                          </React.Fragment>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="table-dark">
                          <td colSpan={4} className="fw-bold">TOTAL — {records.length} requests</td>
                          <td className="text-end fw-bold">{fmt(records.reduce((s, r) => s + Number(r.gross || 0), 0))}</td>
                          <td className="text-end fw-bold">{fmt(records.reduce((s, r) => s + Number(r.charge || 0), 0))}</td>
                          <td className="text-end fw-bold">{fmt(records.reduce((s, r) => s + Number(r.amount || 0), 0))}</td>
                          <td className="text-end fw-bold">{fmt(records.reduce((s, r) => s + Number(r.amount || 0), 0) * rate)}</td>
                          <td colSpan={5} className="small">
                            {selectableIds.length ? `${selectableIds.length} still to pay` : ''}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </>
            ) : null}
          </Col>
        </Row>
      </Container>
    </div>
  );
}
